/**
 * @fileoverview Create-requirements permission toggle handlers for company catalog rows.
 * @module hooks/companyCatalog/useCompanyCatalogPermissions
 */
import { useCallback, useState } from "react";
import { updateCompanyCreateRequirementsPermissions } from "../../logic/companyCatalog/index.js";
import { logger, sanitizeError } from "../../utils/logger.js";

/**
 * Persists create-requirements permission changes and keeps the in-memory catalog
 * list in sync while a row toggle is in flight.
 *
 * @param {Object} options Hook dependencies from the parent catalog composer
 * @param {import('react-router').NavigateFunction} options.navigate Router navigate for permission API calls
 * @param {Array<Object>} options.companiesData Current catalog rows shown in the table
 * @param {import('react').Dispatch<import('react').SetStateAction<Array<Object>>>} options.setCompaniesData Updates catalog rows after a successful toggle
 * @returns {{
 *   togglingCompanyId: string|null,
 *   handleCreateRequirementsChange: (event: import('react').ChangeEvent<HTMLInputElement>) => void
 * }} Row-level loading id and checkbox change handler for collaborators
 */
export const useCompanyCatalogPermissions = ({
  navigate,
  companiesData,
  setCompaniesData,
}) => {
  const [togglingCompanyId, setTogglingCompanyId] = useState(null);

  const handleCreateRequirementsToggle = useCallback(
    async (company, newValue) => {
      const companyId = company._id;
      setTogglingCompanyId(companyId);
      try {
        const updatedCompany = await updateCompanyCreateRequirementsPermissions(
          companyId,
          newValue,
          navigate,
        );
        setCompaniesData((prev) =>
          prev.map((c) => (c._id === companyId ? updatedCompany : c)),
        );
      } catch (error) {
        logger.error("Error updating create requirements permissions:", {
          err: sanitizeError(error),
        });
      } finally {
        setTogglingCompanyId(null);
      }
    },
    [navigate, setCompaniesData],
  );

  const handleCreateRequirementsChange = useCallback(
    (event) => {
      const companyId = event.currentTarget.getAttribute("data-company-id");
      const checked = event.target.checked;
      const company = companiesData.find((c) => c._id === companyId);
      if (company) {
        handleCreateRequirementsToggle(company, checked);
      }
    },
    [companiesData, handleCreateRequirementsToggle],
  );

  return {
    togglingCompanyId,
    handleCreateRequirementsChange,
  };
};
